/**
 * 外部接口调用日志（含原始报文）
 *
 * 这是审计备查的核心页面，因此在设计上偏向「证据可读」而不是「好看」：
 *   - 原始请求/响应报文原样展示，不截断、不美化，便于与对方系统对账；
 *   - 超时、熔断、被配额拦截的调用单独可筛，方便定位「外部校验暂不可用」的根因；
 *   - 日志不做软删除，页面只提供筛查与导出，不提供删除入口。
 *
 * 注意：请求报文里的税号、银行账号等敏感字段在写入日志前已被服务端脱敏，
 * 页面看到的就是脱敏后的内容，前端不做也不应做二次还原。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CopyOutlined, FileSearchOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import {
  PermissionCode,
  PROVIDER_LABELS,
  PROVIDER_MODE_DESCRIPTIONS,
  ProviderCode,
  type ProviderCallLog,
  type ProviderCallStatus,
} from '@ilc/shared';
import dayjs from 'dayjs';
import { logApi } from '@/api/endpoints';
import { mockProviderCallList } from '@/mocks';
import { usePagedList } from '@/hooks/usePagedList';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { ProviderModeTag } from '@/components/ProviderModeTag';
import { PageHeader } from '@/components/PageHeader';
import { feedback } from '@/utils/feedback';

interface Query {
  page?: number;
  pageSize?: number;
  provider?: ProviderCode;
  status?: ProviderCallStatus;
  invoiceId?: string;
  dateFrom?: string;
  dateTo?: string;
}

const STATUS_LABELS: Record<ProviderCallStatus, string> = {
  SUCCESS: '成功',
  FAILED: '失败',
  TIMEOUT: '超时',
  THROTTLED: '被限流',
  QUOTA_EXCEEDED: '配额拦截',
  CIRCUIT_OPEN: '熔断',
};

const STATUS_COLORS: Record<ProviderCallStatus, string> = {
  SUCCESS: 'green',
  FAILED: 'red',
  TIMEOUT: 'orange',
  THROTTLED: 'gold',
  QUOTA_EXCEEDED: 'volcano',
  CIRCUIT_OPEN: 'magenta',
};

/** 会把发票标记为「外部校验暂不可用」的调用结果 */
const UNAVAILABLE_STATUSES: ProviderCallStatus[] = [
  'TIMEOUT',
  'THROTTLED',
  'QUOTA_EXCEEDED',
  'CIRCUIT_OPEN',
  'FAILED',
];

function pretty(value: unknown): string {
  if (value === null || value === undefined) return '（无报文）';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function ProviderLogs(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canRead = can(PermissionCode.PROVIDER_LOG_READ);
  const [detail, setDetail] = useState<ProviderCallLog | null>(null);

  const list = usePagedList<ProviderCallLog, Query>({
    loader: (q) => logApi.providerCalls(q),
    fallback: (q) => mockProviderCallList(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const rows = list.list;
  const stats = useMemo(() => {
    const success = rows.filter((r) => r.status === 'SUCCESS').length;
    const abnormal = rows.filter((r) => UNAVAILABLE_STATUSES.includes(r.status)).length;
    const cost = rows.reduce((sum, r) => sum + (r.costCents ?? 0), 0);
    const avgDuration =
      rows.length > 0
        ? Math.round(rows.reduce((sum, r) => sum + (r.durationMs ?? 0), 0) / rows.length)
        : 0;
    return {
      success,
      abnormal,
      cost,
      avgDuration,
      rate: rows.length > 0 ? Math.round((success / rows.length) * 100) : 0,
    };
  }, [rows]);

  const copyPayload = (text: string): void => {
    void navigator.clipboard
      .writeText(text)
      .then(() => feedback.success('报文已复制到剪贴板'))
      .catch(() => feedback.warning('复制失败，请手动选中文本复制'));
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="外部接口调用日志"
        description="OCR、验真、企查查、T+、钉钉的每一次调用记录与原始报文。全量留存、按保留策略归档，不做软删除。"
        tags={<Tag color="geekblue">{rows.length} 条本页记录</Tag>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={list.reload}>
            刷新
          </Button>
        }
      />

      {!canRead ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="当前账号无调用日志查看权限"
          description="原始报文可能包含业务敏感信息，查看需要 provider:log:read 权限。若接口排查需要，请联系技术实施组或租户管理员。"
        />
      ) : null}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="排障提示：结果状态与「外部校验暂不可用」的对应关系"
        description="超时 / 被限流 / 配额拦截 / 熔断 / 失败这五类结果，会让对应校验项在发票详情页标记为「外部校验暂不可用」，此时发票不能自动流转，需要由有权限的人员执行「人工核验并接管」并填写核验说明。"
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="本页成功率" value={stats.rate} suffix="%" valueStyle={{ color: stats.rate < 90 ? '#d46b08' : '#389e0d' }} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="异常调用"
              value={stats.abnormal}
              suffix="次"
              valueStyle={{ color: stats.abnormal > 0 ? '#cf1322' : undefined }}
              prefix={stats.abnormal > 0 ? <WarningOutlined /> : undefined}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="平均耗时" value={stats.avgDuration} suffix="ms" />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="本页计费合计" value={stats.cost / 100} precision={2} prefix="¥" />
          </Card>
        </Col>
      </Row>

      <DataTable<ProviderCallLog>
        storageKey="provider-logs"
        exportFileName="接口调用日志"
        exportColumns={[
          { title: '调用时间', dataIndex: 'calledAt' },
          { title: '接口', value: (r) => PROVIDER_LABELS[r.provider] },
          { title: '运算', dataIndex: 'operation' },
          { title: '结果', value: (r) => STATUS_LABELS[r.status] },
          { title: '业务码', dataIndex: 'resultCode' },
          { title: '结果说明', dataIndex: 'resultMessage' },
          { title: '耗时(ms)', dataIndex: 'durationMs' },
          { title: 'traceId', dataIndex: 'traceId' },
        ]}
        columns={[
          {
            title: '调用时间',
            dataIndex: 'calledAt',
            width: 165,
            fixed: 'left',
            render: (v: string) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v.replace('T', ' ').slice(0, 19)}
              </span>
            ),
          },
          {
            title: '接口',
            dataIndex: 'provider',
            width: 120,
            render: (v: ProviderCode, record) => (
              <Space direction="vertical" size={0}>
                <span>{PROVIDER_LABELS[v]}</span>
                {/* 从前这里是内联三元（REAL? 沙箱? 否则一律「人工」）。
                    新增 LOCAL / UNAVAILABLE 之后，那种写法会把它们**静默显示成「人工」**——
                    与本次要修的问题同源：未知值落到"看起来正常"的分支。 */}
                <ProviderModeTag mode={record.mode} showImpl={false} />
              </Space>
            ),
          },
          {
            title: '接口方法',
            dataIndex: 'operation',
            width: 180,
            render: (v: string) => (
              <span className="ilc-mono" style={{ fontSize: 12 }}>
                {v}
              </span>
            ),
          },
          {
            title: '结果',
            dataIndex: 'status',
            width: 110,
            render: (v: ProviderCallStatus, record) => (
              <Tooltip title={record.resultMessage ?? ''}>
                <Tag color={STATUS_COLORS[v]} icon={v === 'SUCCESS' ? undefined : <WarningOutlined />}>
                  {STATUS_LABELS[v]}
                </Tag>
              </Tooltip>
            ),
          },
          {
            title: '业务码 / 说明',
            key: 'result',
            width: 240,
            render: (_v, record) => (
              <Space direction="vertical" size={0}>
                {record.resultCode ? (
                  <span className="ilc-mono" style={{ fontSize: 12 }}>
                    {record.resultCode}
                  </span>
                ) : null}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {record.resultMessage ?? '-'}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: '耗时',
            dataIndex: 'durationMs',
            width: 100,
            align: 'right',
            render: (v: number | null | undefined) =>
              v === null || v === undefined ? (
                '-'
              ) : (
                <span className="ilc-num" style={{ color: v > 3000 ? '#cf1322' : undefined }}>
                  {v.toLocaleString('zh-CN')} ms
                </span>
              ),
          },
          {
            title: '重试',
            dataIndex: 'retryCount',
            width: 80,
            align: 'right',
            render: (v: number) => (v > 0 ? <Tag color="orange">{v} 次</Tag> : <span className="ilc-num">0</span>),
          },
          {
            title: '计费',
            dataIndex: 'costCents',
            width: 110,
            align: 'right',
            render: (v: number | null, record) =>
              record.billable ? (
                <span className="ilc-num">¥{((v ?? 0) / 100).toFixed(2)}</span>
              ) : (
                <Tooltip title="沙箱 / 人工兜底 / 被配额拦截的调用不计费">
                  <Typography.Text type="secondary">不计费</Typography.Text>
                </Tooltip>
              ),
          },
          {
            title: '关联发票',
            dataIndex: 'invoiceId',
            width: 150,
            render: (v: string | null) =>
              v ? (
                <span className="ilc-mono" style={{ fontSize: 12 }}>
                  {v}
                </span>
              ) : (
                <Typography.Text type="secondary">-</Typography.Text>
              ),
          },
          {
            title: 'traceId',
            dataIndex: 'traceId',
            width: 170,
            render: (v: string | null) =>
              v ? (
                <Typography.Text className="ilc-mono" style={{ fontSize: 12 }} copyable>
                  {v}
                </Typography.Text>
              ) : (
                '-'
              ),
          },
          {
            title: '操作',
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_v, record) => (
              <Button size="small" type="link" icon={<FileSearchOutlined />} onClick={() => setDetail(record)}>
                原始报文
              </Button>
            ),
          },
        ]}
        dataSource={rows}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        emptyText="没有匹配的调用记录，试试切换接口或结果状态"
        toolbar={
          <Space wrap>
            <Select
              placeholder="接口"
              allowClear
              style={{ width: 150 }}
              value={list.query.provider}
              onChange={(value) => list.setQuery({ provider: value })}
              options={Object.entries(PROVIDER_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              placeholder="结果状态"
              allowClear
              style={{ width: 150 }}
              value={list.query.status}
              onChange={(value) => list.setQuery({ status: value })}
              options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Input.Search
              placeholder="关联发票 ID / 单号"
              allowClear
              style={{ width: 200 }}
              onSearch={(value) => list.setQuery({ invoiceId: value })}
            />
            <Select
              placeholder="调用时间范围"
              allowClear
              style={{ width: 180 }}
              value={
                list.query.dateFrom && list.query.dateTo
                  ? `${list.query.dateFrom}-${list.query.dateTo}`
                  : undefined
              }
              onChange={(value: string | undefined) => {
                if (!value) {
                  list.setQuery({ dateFrom: undefined, dateTo: undefined });
                  return;
                }
                const [from, to] = value.split('-');
                list.setQuery({ dateFrom: from, dateTo: to });
              }}
              options={[
                { value: `${dayjs().format('YYYY-MM-DD')}-${dayjs().format('YYYY-MM-DD')}`, label: '今天' },
                {
                  value: `${dayjs().subtract(7, 'day').format('YYYY-MM-DD')}-${dayjs().format('YYYY-MM-DD')}`,
                  label: '近 7 天',
                },
                {
                  value: `${dayjs().subtract(30, 'day').format('YYYY-MM-DD')}-${dayjs().format('YYYY-MM-DD')}`,
                  label: '近 30 天',
                },
              ]}
            />
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Drawer
        open={detail !== null}
        title={
          <Space wrap>
            <span>原始报文</span>
            {detail ? <Tag color="geekblue">{PROVIDER_LABELS[detail.provider]}</Tag> : null}
            {detail ? <Tag color={STATUS_COLORS[detail.status]}>{STATUS_LABELS[detail.status]}</Tag> : null}
          </Space>
        }
        onClose={() => setDetail(null)}
        width={760}
        extra={
          detail ? (
            <Space>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() =>
                  copyPayload(
                    [
                      `# 请求\n${pretty(detail.requestPayload)}`,
                      `# 响应\n${pretty(detail.responsePayload)}`,
                      detail.errorStack ? `# 异常栈\n${detail.errorStack}` : '',
                    ]
                      .filter(Boolean)
                      .join('\n\n'),
                  )
                }
              >
                复制全部
              </Button>
            </Space>
          ) : null
        }
      >
        {detail ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="调用时间">
                <span className="ilc-mono">{detail.calledAt.replace('T', ' ').slice(0, 19)}</span>
              </Descriptions.Item>
              <Descriptions.Item label="接口方法">
                <span className="ilc-mono">{detail.operation}</span>
              </Descriptions.Item>
              <Descriptions.Item label="运行模式">
                <ProviderModeTag mode={detail.mode} showImpl={false} />
                <Typography.Text type="secondary" style={{ marginInlineStart: 8, fontSize: 12 }}>
                  {PROVIDER_MODE_DESCRIPTIONS[detail.mode] ?? ''}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="耗时 / 重试">
                <span className="ilc-num">{detail.durationMs ?? '-'} ms</span> · {detail.retryCount} 次
              </Descriptions.Item>
              <Descriptions.Item label="通道业务码">
                <span className="ilc-mono">{detail.resultCode ?? '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="结果说明">{detail.resultMessage ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="关联业务">
                <span className="ilc-mono">
                  {detail.bizType ?? '-'} / {detail.bizId ?? '-'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="traceId">
                <Typography.Text className="ilc-mono" copyable>
                  {detail.traceId ?? '-'}
                </Typography.Text>
              </Descriptions.Item>
            </Descriptions>

            {UNAVAILABLE_STATUSES.includes(detail.status) ? (
              <Alert
                type="warning"
                showIcon
                message="该调用异常会导致对应校验项「外部校验暂不可用」"
                description="发票详情页会展示「人工核验并接管」入口，由有权限的人员填写核验说明后继续流转；报告页会记录接管说明，不影响后续审计追溯。"
              />
            ) : null}

            <Card
              size="small"
              title="请求报文"
              extra={
                <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => copyPayload(pretty(detail.requestPayload))}>
                  复制
                </Button>
              }
            >
              <pre className="ilc-mono ilc-payload">{pretty(detail.requestPayload)}</pre>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                税号、银行账号等敏感字段在写入日志前已由服务端脱敏，页面看到的就是归档内容。
              </Typography.Text>
            </Card>

            <Card
              size="small"
              title="响应原始报文（审计证据）"
              extra={
                <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => copyPayload(pretty(detail.responsePayload))}>
                  复制
                </Button>
              }
            >
              <pre className="ilc-mono ilc-payload">{pretty(detail.responsePayload)}</pre>
            </Card>

            {detail.errorStack ? (
              <Card size="small" title="异常栈">
                <pre className="ilc-mono ilc-payload">{detail.errorStack}</pre>
              </Card>
            ) : null}
          </Space>
        ) : null}
      </Drawer>

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        日志保留策略：接口调用日志全量留存并按保留策略归档，页面不提供删除入口；
        如确需清理，请通过运维流程执行并同步在平台审计日志中记录。
      </Typography.Paragraph>
    </div>
  );
}
